/**
 * The record sheet anatomy (§6.1, §7.4). Every feature sheet body renders one:
 *
 * - a toolbar (44 px, 56 px on phones): Back, the stack breadcrumb, the width
 *   toggle, Open full page, an overflow menu, Close;
 * - the hero: the human fact, a status pill, a meta line;
 * - optional alert and pending-changes strips;
 * - the body: sections, or underline tabs addressed by `stab`;
 * - an optional sticky footer for actions or the decision panel.
 *
 * Loading shows the skeleton at final layout, an error shows `ErrorState` in
 * the body while the toolbar keeps working (§6.1). Opening a record moves focus
 * to its title; `j` / `k` and the stack do the same for the record they land on.
 */
import {
  ArrowExpand01Icon,
  ArrowLeft01Icon,
  ArrowShrink01Icon,
  Cancel01Icon,
  Copy01Icon,
  LinkSquare02Icon,
  MoreHorizontalIcon,
} from '@hugeicons/core-free-icons';
import { type ReactNode, type RefObject, useEffect, useRef } from 'react';
import {
  ErrorState,
  type ErrorStateProps,
} from '@/components/feedback/error-state';
import { SkeletonSheet } from '@/components/feedback/skeletons';
import { Icon } from '@/components/primitives/icon';
import { Kbd } from '@/components/primitives/kbd';
import { SHEET_REGISTRY } from '@/components/sheet/registry';
import { SheetBreadcrumb } from '@/components/sheet/sheet-breadcrumb';
import {
  rememberSheetTitle,
  useSheetTitles,
} from '@/components/sheet/sheet-titles';
import { SheetScopeContext, useSheet } from '@/components/sheet/use-sheet';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { copyText } from '@/lib/copy';
import type { IconSvgObject } from '@/lib/icon-types';
import { type SheetRef, sheetKey } from '@/lib/sheet-url';
import { cn } from '@/lib/utils';

interface RecordSheetTab {
  id: string;
  label: string;
  content: ReactNode;
}

export interface RecordSheetProps {
  sheetRef: SheetRef;
  /** The human fact: "GHS 250.00 withdrawal to MTN 054•••••12". Never a raw id. */
  title: string;
  /** The hero figure (amount or name) above the title. */
  hero?: ReactNode;
  status?: ReactNode;
  /** Time, actor, copyable id and reference chips. */
  meta?: ReactNode;
  /** Failure, stuck or changed-since strip. */
  alert?: ReactNode;
  /** The pending-changes strip. */
  pending?: ReactNode;
  tabs?: RecordSheetTab[];
  children?: ReactNode;
  /** Sticky actions or the decision panel. */
  footer?: ReactNode;
  /** The full page (hash href), when the type has one. */
  fullPageHref?: string;
  loading?: boolean;
  error?: ErrorStateProps;
}

export function RecordSheet({
  sheetRef,
  title,
  hero,
  status,
  meta,
  alert,
  pending,
  tabs,
  children,
  footer,
  fullPageHref,
  loading = false,
  error,
}: Readonly<RecordSheetProps>) {
  const sheet = useSheet();
  const titles = useSheetTitles();
  const rootRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const key = sheetKey(sheetRef);
  const ready = !loading && !error;
  const stack = sheet.stack.length > 0 ? sheet.stack : [sheetRef];
  const depth = stack.length;
  const noun = SHEET_REGISTRY[sheetRef.type].noun;

  // File the title so the records above this one can name it in their crumbs.
  useEffect(() => {
    if (ready) rememberSheetTitle(sheetRef, title);
  }, [ready, sheetRef, title]);

  useOpenFocus(rootRef, titleRef, key, ready);

  const crumbs = stack.map((ref, index) => {
    const refKey = sheetKey(ref);
    const isCurrent = index === depth - 1;
    return {
      key: refKey,
      label: isCurrent
        ? title
        : (titles[refKey] ?? SHEET_REGISTRY[ref.type].noun),
    };
  });
  const parentLabel = crumbs.at(-2)?.label;

  return (
    <SheetScopeContext value={sheetRef}>
      <div
        ref={rootRef}
        data-record-sheet={key}
        tabIndex={-1}
        className="flex h-full min-h-0 flex-col bg-card outline-none"
      >
        <Toolbar
          crumbs={crumbs}
          depth={depth}
          parentLabel={parentLabel}
          fullPageHref={fullPageHref}
        />

        <div
          data-sheet-body
          aria-busy={loading || undefined}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        >
          {loading ? <SkeletonSheet /> : null}
          {!loading && error ? (
            <div className="p-4">
              <ErrorState {...error} />
            </div>
          ) : null}
          {ready ? (
            <SheetLoaded
              titleRef={titleRef}
              noun={noun}
              title={title}
              hero={hero}
              status={status}
              meta={meta}
              alert={alert}
              pending={pending}
              tabs={tabs}
            >
              {children}
            </SheetLoaded>
          ) : null}
        </div>

        {footer && ready ? (
          <div className="max-h-[min(60%,30rem)] shrink-0 overflow-y-auto overscroll-contain border-t border-border bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </div>
        ) : null}
      </div>
    </SheetScopeContext>
  );
}

// ---------------------------------------------------------------------------
// Body

/** Opening a record moves focus to its title (§6.1). While it loads, focus
 * waits on the sheet itself so keys land inside it. Skipped once the person
 * has moved into the sheet's content. */
function useOpenFocus(
  rootRef: RefObject<HTMLDivElement | null>,
  titleRef: RefObject<HTMLHeadingElement | null>,
  key: string,
  ready: boolean,
) {
  const focusedFor = useRef<string | null>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      root.contains(active) &&
      active !== root &&
      active !== titleRef.current
    )
      return;
    const stage = ready ? `${key}|title` : `${key}|root`;
    if (focusedFor.current === stage) return;
    if (!ready && focusedFor.current?.startsWith(`${key}|`)) return;
    focusedFor.current = stage;
    if (ready) titleRef.current?.focus({ preventScroll: true });
    else root.focus({ preventScroll: true });
  }, [ready, key, rootRef, titleRef]);
}

function resolveTab(
  tabs: RecordSheetTab[],
  current: string | undefined,
): string | undefined {
  return tabs.some((tab) => tab.id === current) ? current : tabs[0]?.id;
}

interface SheetLoadedProps {
  titleRef: RefObject<HTMLHeadingElement | null>;
  noun: string;
  title: string;
  hero?: ReactNode;
  status?: ReactNode;
  meta?: ReactNode;
  alert?: ReactNode;
  pending?: ReactNode;
  tabs?: RecordSheetTab[];
  children?: ReactNode;
}

function SheetLoaded({
  titleRef,
  noun,
  title,
  hero,
  status,
  meta,
  alert,
  pending,
  tabs,
  children,
}: Readonly<SheetLoadedProps>) {
  const sheet = useSheet();
  return (
    <>
      <header className="flex flex-col gap-2 px-4 pt-4 pb-4">
        {hero ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div className="min-w-0 text-2xl">{hero}</div>
            {status}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h2
            ref={titleRef}
            tabIndex={-1}
            data-sheet-title
            className={cn(
              'min-w-0 break-words text-foreground outline-none',
              hero ? 'text-lg' : 'text-xl',
            )}
          >
            {title}
          </h2>
          {hero ? null : status}
        </div>
        {meta ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {meta}
          </div>
        ) : null}
      </header>
      {alert || pending ? (
        <div className="flex flex-col gap-2 px-4 pb-4">
          {alert}
          {pending}
        </div>
      ) : null}
      {tabs && tabs.length > 0 ? (
        <Tabs
          value={resolveTab(tabs, sheet.tab)}
          onValueChange={(value) => sheet.setTab(String(value))}
          className="gap-0"
        >
          <TabsList
            variant="line"
            aria-label={`${noun} sections`}
            className="sticky top-0 z-10 w-full justify-start gap-5 overflow-x-auto rounded-none border-y border-border bg-card p-0 px-4 group-data-horizontal/tabs:h-10"
          >
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="h-10 flex-none rounded-none px-0 text-base font-medium text-muted-foreground group-data-horizontal/tabs:after:bottom-0 data-active:font-semibold data-active:text-foreground group-data-[variant=line]/tabs-list:after:bg-brand dark:data-active:text-foreground"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((tab) => (
            <TabsContent
              key={tab.id}
              value={tab.id}
              className="flex flex-col gap-6 p-4"
            >
              {tab.content}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <div className="flex flex-col gap-6 px-4 pb-4">{children}</div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Toolbar

interface ToolbarProps {
  crumbs: { key: string; label: string }[];
  depth: number;
  parentLabel: string | undefined;
  fullPageHref: string | undefined;
}

function Toolbar({
  crumbs,
  depth,
  parentLabel,
  fullPageHref,
}: Readonly<ToolbarProps>) {
  const sheet = useSheet();
  const wide = sheet.width === 'wide';
  const deep = depth > 1;

  return (
    <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2 md:h-11">
      {deep ? (
        <ToolbarButton
          label={parentLabel ? `Back to ${parentLabel}` : 'Back'}
          tip="Back"
          keys="alt+arrowleft"
          icon={ArrowLeft01Icon}
          onClick={sheet.pop}
        />
      ) : (
        // A phone sheet has no scrim to tap, so its first control closes it.
        <ToolbarButton
          label="Close"
          tip="Close"
          keys="escape"
          icon={Cancel01Icon}
          onClick={sheet.close}
          className="md:hidden"
        />
      )}
      <SheetBreadcrumb
        className="min-w-0 flex-1 px-1"
        items={crumbs}
        onSelect={sheet.popTo}
      />
      <ToolbarButton
        label={wide ? 'Make the sheet narrower' : 'Make the sheet wider'}
        tip={wide ? 'Narrower' : 'Wider'}
        keys="w"
        icon={wide ? ArrowShrink01Icon : ArrowExpand01Icon}
        onClick={() => sheet.setWidth(wide ? 'standard' : 'wide')}
        className="hidden md:inline-flex"
      />
      {fullPageHref ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <a
                href={fullPageHref}
                aria-label="Open full page"
                className={cn(
                  buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
                  'hidden md:inline-flex',
                )}
              />
            }
          >
            <Icon icon={LinkSquare02Icon} size={16} />
          </TooltipTrigger>
          <TooltipContent>Open full page</TooltipContent>
        </Tooltip>
      ) : null}
      <OverflowMenu depth={depth} fullPageHref={fullPageHref} />
      <ToolbarButton
        label="Close"
        tip="Close"
        keys="escape"
        icon={Cancel01Icon}
        onClick={sheet.close}
        className={deep ? undefined : 'hidden md:inline-flex'}
      />
    </div>
  );
}

interface ToolbarButtonProps {
  /** The accessible name. */
  label: string;
  /** The tooltip text, shorter than the label. */
  tip: string;
  keys?: string;
  icon: IconSvgObject;
  onClick: () => void;
  className?: string;
}

function ToolbarButton({
  label,
  tip,
  keys,
  icon,
  onClick,
  className,
}: Readonly<ToolbarButtonProps>) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            onClick={onClick}
            className={className}
          />
        }
      >
        <Icon icon={icon} size={16} />
      </TooltipTrigger>
      <TooltipContent>
        {tip}
        {keys ? <Kbd keys={keys} /> : null}
      </TooltipContent>
    </Tooltip>
  );
}

function OverflowMenu({
  depth,
  fullPageHref,
}: {
  depth: number;
  fullPageHref: string | undefined;
}) {
  const sheet = useSheet();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="More actions"
          />
        }
      >
        <Icon icon={MoreHorizontalIcon} size={16} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuItem
          onClick={() => void copyText(window.location.href, 'link')}
        >
          <Icon icon={Copy01Icon} size={16} />
          Copy link to this record
        </DropdownMenuItem>
        {fullPageHref ? (
          <DropdownMenuItem
            className="md:hidden"
            render={<a href={fullPageHref} aria-label="Open full page" />}
          >
            <Icon icon={LinkSquare02Icon} size={16} />
            Open full page
          </DropdownMenuItem>
        ) : null}
        {depth > 1 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={sheet.close}>
              <Icon icon={Cancel01Icon} size={16} />
              Close all records
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
